import { useId, useMemo } from 'react';

import {
  Button,
  DialogTrigger,
  Label,
  ListBox,
  ListBoxItem,
  ListBoxItemProps,
  Popover,
} from 'react-aria-components';
import classNames from 'classnames';

import { DownArrowIcon } from '../icons';

import * as styles from './MultiSelectInput.css';

export type MultiSelectInputOption<TOptionKey extends string> = {
  /**
   * The name/content to display for the option in the dropdown
   */
  name: string | React.ReactNode;
  /**
   * A text-only readable name for the option, used for accessibility purposes.
   */
  textName?: string;
  /**
   * The unique key to represent each option
   */
  key: TOptionKey;
  /**
   * Additional props to pass to the ListBoxItem component for this option
   */
  itemProps?: Omit<ListBoxItemProps, 'children'> & {
    // Allow any data-* attributes to be passed through
    [key: `data-${string}`]: string;
  };
} & (
  | {
      name: string;
      textName?: string;
    }
  | {
      // If the name is a React node, we must supply an additional string-only textName for screen readers
      name: React.ReactNode;
      textName: string;
    }
);

interface MultiSelectInputProps<TOptionKey extends string> {
  id?: string;
  label?: React.ReactNode;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  className?: string;
  buttonClassName?: string;
  labelClassName?: string;
  listBoxClassName?: string;
  /**
   * Class name applies to the span wrapping the currently displayed selected value
   * label for the select input
   */
  selectedValueWrapperClassName?: string;
  /**
   * Additional props to apply to each ListBoxItem component for each option
   */
  optionItemProps?: Omit<ListBoxItemProps, 'children'>;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  placeholder?: string;
  isDisabled?: boolean;
  options: ReadonlyArray<MultiSelectInputOption<TOptionKey>>;
  /**
   * The currently selected key for the select input. This can be null
   * if no option is selected.
   */
  selectedKeys: TOptionKey[] | null;
  onSelectionChange: (newSelectedKeys: TOptionKey[]) => void;
  getMultipleSelectionsLabel?: (selectedKeys: TOptionKey[]) => React.ReactNode;
}

const defaultGetMultipleSelectionsLabel = (selectedKeys: string[]) =>
  `${selectedKeys.length} items selected`;

/**
 * A controlled select input component that allows users to select from a list of options with multi-select support.
 * Multi select controls: Shift+click / Ctrl+click (or Cmd+click on Mac) will add or remove individual options to/from the selection.
 */
export const MultiSelectInput = <TOptionKey extends string>({
  label,
  ariaLabel,
  ariaLabelledBy,
  options,
  selectedKeys,
  onSelectionChange,
  isOpen,
  onOpenChange,
  buttonClassName,
  listBoxClassName,
  selectedValueWrapperClassName,
  optionItemProps,
  labelClassName,
  placeholder,
  getMultipleSelectionsLabel = defaultGetMultipleSelectionsLabel,
}: MultiSelectInputProps<TOptionKey>) => {
  const selectValueContent = useMemo(() => {
    if (selectedKeys === null || selectedKeys.length === 0) {
      return placeholder ?? 'Select an option';
    }

    if (selectedKeys.length === 1) {
      const selectedKey = selectedKeys[0];
      const selectedOption = options.find(({ key }) => key === selectedKey);
      if (selectedOption) {
        return selectedOption.name;
      }
    }

    return getMultipleSelectionsLabel(selectedKeys);
  }, [getMultipleSelectionsLabel, options, placeholder, selectedKeys]);

  const buttonID = useId();

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={onOpenChange}>
      {label ? <Label className={classNames(styles.label, labelClassName)}>{label}</Label> : null}
      <Button
        className={classNames(styles.selectButton, buttonClassName)}
        id={buttonID}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        onKeyDown={(e) => {
          // Open the select popover when the user presses up/down arrow while focused on the button
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            (e.target as HTMLButtonElement).click();
          }
        }}
      >
        <span className={classNames(styles.selectedValue, selectedValueWrapperClassName)}>
          {selectValueContent}
        </span>
        <DownArrowIcon aria-hidden="true" className={styles.arrowIcon} />
      </Button>
      <Popover className={styles.popover}>
        <ListBox
          aria-labelledby={buttonID}
          items={options}
          selectedKeys={selectedKeys ?? undefined}
          selectionMode="multiple"
          selectionBehavior="replace"
          disallowEmptySelection={false}
          onSelectionChange={(selection) => {
            if (selection === 'all') {
              onSelectionChange(options.map((option) => option.key));
            } else {
              onSelectionChange(Array.from(selection) as TOptionKey[]);
            }
          }}
          className={classNames(styles.listBox, listBoxClassName)}
        >
          {(option) => {
            const listBoxItemProps = {
              // Apply base option props, then option-specific props
              ...optionItemProps,
              ...option.itemProps,
            };
            // Ensure all classNames are merged
            listBoxItemProps.className = classNames(
              styles.listBoxItem,
              optionItemProps?.className,
              option.itemProps?.className,
            );

            return (
              <ListBoxItem {...listBoxItemProps} textValue={option.textName}>
                {option.name}
              </ListBoxItem>
            );
          }}
        </ListBox>
      </Popover>
    </DialogTrigger>
  );
};
